import { useState, useEffect } from 'react';
import AppLayout from '../components/AppLayout';
import { AlertTriangle, DollarSign, TrendingUp } from 'lucide-react';
import { apiFetch } from '../lib/api';
import type { Finding } from '../../shared/types';

export default function Findings() {
  const [findings, setFindings] = useState<Finding[]>([]);

  useEffect(() => {
    const fetchFindings = async () => {
      try {
        const data = await apiFetch<Finding[]>('/api/findings');
        setFindings(data);
      } catch (error) {
        console.error('Failed to fetch findings:', error);
      }
    };

    fetchFindings();
  }, []);

  const totalAmount = findings.reduce((sum, f) => sum + (f.amount || 0), 0);
  const highSeverity = findings.filter((f) => f.severity === 'HIGH').length;

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Revenue Protection Findings</h1>
          <p className="text-slate-400">Issues detected by the automated scanner</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
              </div>
            </div>
            <p className="text-sm text-slate-400 mb-1">Total Findings</p>
            <p className="text-3xl font-bold text-white">{findings.length}</p>
          </div>

          <div className="bg-gradient-to-br from-red-500/10 to-pink-500/10 border border-red-500/20 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-red-400" />
              </div>
            </div>
            <p className="text-sm text-slate-400 mb-1">High Severity</p>
            <p className="text-3xl font-bold text-white">{highSeverity}</p>
          </div>

          <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border border-emerald-500/20 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-400" />
              </div>
            </div>
            <p className="text-sm text-slate-400 mb-1">Potential Savings</p>
            <p className="text-3xl font-bold text-white">${totalAmount.toFixed(2)}</p>
          </div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-900/50 border-b border-slate-700/50">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Type</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Description</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Amount</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Severity</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Status</th>
              </tr>
            </thead>
            <tbody>
              {findings.length > 0 ? (
                findings.map((finding) => (
                  <tr key={finding.id} className="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors">
                    <td className="p-4">
                      <span className="text-sm font-medium text-white">{finding.finding_type}</span>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-slate-300">{finding.description}</span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-sm text-white">
                        <DollarSign className="w-4 h-4 text-slate-400" />
                        {finding.amount?.toFixed(2) || '0.00'}
                      </div>
                    </td>
                    <td className="p-4">
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          finding.severity === 'HIGH' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                          finding.severity === 'MEDIUM' ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' :
                          'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                        }`}
                      >
                        {finding.severity}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-xs px-2 py-1 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                        {finding.status || 'OPEN'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="p-12 text-center text-slate-500">
                    No findings detected yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AppLayout>
  );
}
